import { WORLD_PROP_TEMPLATES } from './worldPropTemplates';
import { SETTLEMENT_PROP_TEMPLATES } from './settlementPropTemplates';
import { OPENING_AREA_PROP_TEMPLATES } from './openingAreaPropTemplates';
import { RUINS_PROP_TEMPLATES } from './ruinsPropTemplates';
import { FENWATCH_PROP_TEMPLATES } from './fenwatchPropTemplates';
import { SETTLEMENT_DETAIL_TEMPLATES } from './settlementDetailTemplates';
import { FACADE_PROP_TEMPLATES } from './facadePropTemplates';
import { HARBOR_PROP_TEMPLATES } from './harborPropTemplates';
import { COIL_PROP_TEMPLATES } from './coilPropTemplates';
import { CAVERN_PROP_TEMPLATES } from './cavernPropTemplates';
import { COASTAL_DETAIL_TEMPLATES } from './coastalDetailTemplates';
import { CAVERN_FIXTURE_TEMPLATES } from './cavernFixtureTemplates';
import { HUSH_PROP_TEMPLATES } from './pieceCatalog/hush';
import { YARD_PROP_TEMPLATES } from './pieceCatalog/yard';
import { MILLRACE_PROP_TEMPLATES } from './pieceCatalog/millrace';
import { LOOT_PROP_TEMPLATES } from './pieceCatalog/loot';
import { COURSE_PROP_TEMPLATES } from './pieceCatalog/course';
import { TIDAL_CASCADE_PROP_TEMPLATES } from './tidalCascadePropTemplates';
import { ROUTE_PROP_TEMPLATES } from './routeProps/generatedRoutePropTemplates';

/** Pure authoring catalog. Renderer and map validators share the same names and kinds. */
export const TOWN_PROP_TEMPLATES = {
  gable_inn: { kind: 'village_house', name: 'Gabled inn', frame: { w: 90, h: 100 }, band: 'vegetation' },
  cross_gable_house: { kind: 'village_house', name: 'Cross-gabled house', frame: { w: 90, h: 100 }, band: 'vegetation' },
  timber_hall: { kind: 'village_house', name: 'Timber hall', frame: { w: 90, h: 100 }, band: 'vegetation' },
  crook_lantern: { kind: 'lantern_post', name: 'Crook lantern', frame: { w: 56, h: 112 }, band: 'vegetation' },
  twin_lantern: { kind: 'lantern_post', name: 'Twin street lantern', frame: { w: 56, h: 112 }, band: 'vegetation' },
  storm_lantern: { kind: 'lantern_post', name: 'Caged harbor lantern', frame: { w: 56, h: 112 }, band: 'vegetation' },
  quay_cargo: { kind: 'dock', name: 'Quay cargo and moorings', frame: { w: 140, h: 56 }, band: 'vegetation' },
  trainer_hall: { kind: 'village_house', name: 'Trainer hall frontage', frame: { w: 238, h: 144 }, band: 'architecture',
    wallRegions: [{ x: 2, y: 68, width: 89, height: 61 }, { x: 147, y: 68, width: 89, height: 61 }] },
  market_stall: { kind: 'village_house', name: 'Provisioning market stall', frame: { w: 100, h: 126 }, band: 'architecture' },
  hanging_lantern: { kind: 'lantern_post', name: 'Ceiling-hung lantern', frame: { w: 22, h: 44 }, band: 'architecture', attachment: 'ceiling' },
  harbor_shopfront: { kind: 'village_house', name: 'Harbor shop frontage', frame: { w: 200, h: 180 }, band: 'architecture' },
  lantern_hall: { kind: 'village_house', name: 'Lantern hall frontage', frame: { w: 200, h: 380 }, band: 'architecture' },
  bracket_lantern: { kind: 'lantern_post', name: 'Wall bracket lantern', frame: { w: 44, h: 58 }, band: 'architecture',
    fixture: true, attachment: 'wall', mountPatch: { x: 1, y: 6, width: 4, height: 20 } },
  shop_sign: { kind: 'sign', name: 'Hanging shop sign', frame: { w: 72, h: 44 }, band: 'architecture',
    fixture: true, attachment: 'ceiling' },
  ...SETTLEMENT_PROP_TEMPLATES,
  ...OPENING_AREA_PROP_TEMPLATES,
  ...RUINS_PROP_TEMPLATES,
  ...FENWATCH_PROP_TEMPLATES,
  ...SETTLEMENT_DETAIL_TEMPLATES,
  ...FACADE_PROP_TEMPLATES,
  ...HARBOR_PROP_TEMPLATES,
  ...COIL_PROP_TEMPLATES,
  ...CAVERN_PROP_TEMPLATES,
  ...COASTAL_DETAIL_TEMPLATES,
  ...CAVERN_FIXTURE_TEMPLATES,
  ...HUSH_PROP_TEMPLATES,
  ...YARD_PROP_TEMPLATES,
  ...MILLRACE_PROP_TEMPLATES,
  ...LOOT_PROP_TEMPLATES,
  ...COURSE_PROP_TEMPLATES,
  ...TIDAL_CASCADE_PROP_TEMPLATES,
  ...ROUTE_PROP_TEMPLATES,
  ...WORLD_PROP_TEMPLATES,
} as const;
export type TownPropTemplate = keyof typeof TOWN_PROP_TEMPLATES;
export type TownHouseTemplate = 'gable_inn' | 'cross_gable_house' | 'timber_hall';
export type TownLanternTemplate = 'crook_lantern' | 'twin_lantern' | 'storm_lantern';
export type TownPropRenderBand = typeof TOWN_PROP_TEMPLATES[TownPropTemplate]['band'];
/** `hook`: decorative hang from a visible host's catalog hookPoint (no collision). */
export type TownPropAttachment = 'ground' | 'ceiling' | 'wall' | 'surface' | 'backdrop' | 'hook';
export { roofFacadePlacement } from './roofFacadePlacement';

export const TOWN_PROP_PALETTE_KEYS = ['primary', 'shade', 'roof', 'roofEdge', 'wood', 'woodEdge', 'stone', 'iron', 'glass', 'glassCore'] as const;
export type TownPropPaletteKey = typeof TOWN_PROP_PALETTE_KEYS[number];
export type TownPropPalette = Readonly<Record<TownPropPaletteKey, number>>;
export type TownPropPaletteOverrides = Readonly<Partial<Record<TownPropPaletteKey, number | string>>>;

/** Author colors are exact RGB integers or six-digit #RRGGBB strings, including black. */
export function parseTownPropColor(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0xffffff) return value;
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? Number.parseInt(value.slice(1), 16) : undefined;
}
export function shadeTownColor(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/** Shared defaults keep existing art stable; valid overrides are absolute, never deltas. */
export function resolveTownPropPalette(tint: number, overrides?: TownPropPaletteOverrides): TownPropPalette {
  const input: TownPropPaletteOverrides = overrides && typeof overrides === 'object' && !Array.isArray(overrides) ? overrides : {};
  const primary = parseTownPropColor(input.primary) ?? parseTownPropColor(tint) ?? 0;
  const palette: Record<TownPropPaletteKey, number> = {
    primary, shade: shadeTownColor(primary, 0.7), roof: 0x414257, roofEdge: 0x817b8f,
    wood: 0x493b3d, woodEdge: 0x927d6b, stone: 0x515463, iron: 0x343a47,
    glass: 0xd6a66a, glassCore: 0xffd89a,
  };
  for (const key of TOWN_PROP_PALETTE_KEYS) palette[key] = parseTownPropColor(input[key]) ?? palette[key];
  if ((input as any).surface) {
    (palette as any).surface = (input as any).surface;
  }
  return Object.freeze(palette);
}

/** Runtime fallback for old/invalid authored data; authoring validation reports the error separately. */
export function resolveTownPropTemplate(kind: string, value: unknown): TownPropTemplate | undefined {
  if (typeof value !== 'string' || !Object.prototype.hasOwnProperty.call(TOWN_PROP_TEMPLATES, value)) return undefined;
  const id = value as TownPropTemplate;
  return TOWN_PROP_TEMPLATES[id].kind === kind ? id : undefined;
}

/** Attachment belongs to compatible catalog art, never an authored bypass flag. */
export function resolveTownPropAttachment(kind: string, value: unknown): TownPropAttachment {
  const id = resolveTownPropTemplate(kind, value);
  const template = id ? TOWN_PROP_TEMPLATES[id] : undefined;
  return template && 'attachment' in template ? template.attachment : 'ground';
}

export function validateScenePropTemplates(props: unknown): Array<{ field: string; message: string; severity: 'error' }> {
  if (props === undefined) return [];
  if (!Array.isArray(props)) return [{ field: 'scene.natureProps', message: 'Expected an array of authored props', severity: 'error' }];
  const errors: Array<{ field: string; message: string; severity: 'error' }> = [];
  props.forEach((prop, index) => {
    if (!prop || typeof prop !== 'object') return;
    const field = `scene.natureProps[${index}].template`;
    const name = prop.template;
    if (name !== undefined && (typeof name !== 'string' || !Object.prototype.hasOwnProperty.call(TOWN_PROP_TEMPLATES, name))) {
      const valid = Object.entries(TOWN_PROP_TEMPLATES).filter(([, value]) => value.kind === prop.kind).map(([id]) => id);
      errors.push({ field, message: `Unknown prop template "${String(name)}". Valid templates for ${String(prop.kind)}: ${valid.join(', ') || 'none'}.`, severity: 'error' });
    } else if (name !== undefined) {
      const kind = TOWN_PROP_TEMPLATES[name as TownPropTemplate].kind;
      if (kind !== prop.kind) errors.push({ field, message: `Template "${name}" belongs to ${kind}, not ${String(prop.kind)}.`, severity: 'error' });
    }
    if (prop.palette === undefined) return;
    const paletteField = `scene.natureProps[${index}].palette`;
    if (!resolveTownPropTemplate(prop.kind, name)) errors.push({ field: paletteField,
      message: 'Palette overrides require a compatible named prop template.', severity: 'error' });
    if (!prop.palette || typeof prop.palette !== 'object' || Array.isArray(prop.palette)) {
      errors.push({ field: paletteField, message: 'Expected an object of named RGB palette colors.', severity: 'error' });
      return;
    }
    for (const [key, value] of Object.entries(prop.palette)) {
      if (!(TOWN_PROP_PALETTE_KEYS as readonly string[]).includes(key)) errors.push({ field: `${paletteField}.${key}`,
        message: `Unknown palette key "${key}". Valid keys: ${TOWN_PROP_PALETTE_KEYS.join(', ')}.`, severity: 'error' });
      else if (parseTownPropColor(value) === undefined) errors.push({ field: `${paletteField}.${key}`,
        message: 'Expected an integer RGB color from 0 to 0xffffff or a six-digit #RRGGBB string.', severity: 'error' });
    }
  });
  return errors;
}
